import React from 'react';
import PropTypes from 'prop-types';
import LoadingOverlay from 'components/ui/LoadingOverlay';
import RadioGroup from 'components/ui/formik/RadioGroup';
import TextArea from 'components/ui/formik/TextArea';
import Button from 'components/ui/buttons/Button';
import { PROJECT_CANCEL_REASONS } from 'consts';
import { projectCancelReasonsValidations } from 'validationSchemas/Project';
import { Field, Form, Formik } from 'formik';
import { useCloseProjectMutation } from 'requests';
import { useQueryClient } from 'react-query';
import { showMessage } from 'utilities';
import { setFieldErrors } from 'utilities/formik';

const CloseProjectForm = ({ onCancel, slug }) => {
  const initialValues = {
    hired: false,
    other: false,
    reason: '',
  };

  const handleRadioChange = (e, setValues, setTouched) => {
    const reasonValue = e.target.value;
    switch (reasonValue) {
      case 'filled':
        setValues({
          hired: true,
          other: false,
          reason: reasonValue,
        });
        break;
      case 'other':
        setTouched({ reason: false });
        setValues({
          hired: false,
          other: true,
          reason: '',
        });
        break;
      default:
        setValues({
          hired: false,
          other: false,
          reason: reasonValue,
        });
        break;
    }
  };

  const mutation = useCloseProjectMutation(slug);
  const queryClient = useQueryClient();

  const submit = (values, formikHelpers) => {
    mutation.mutate(values, {
      onError: ({ errors }) => setFieldErrors(errors, formikHelpers),
      onSuccess: () => {
        queryClient.invalidateQueries(['projects']);
      },
    });
  };

  if (mutation.isError) {
    showMessage('An error has occurred', true);
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={projectCancelReasonsValidations}
      className="remove-project-modal-content px-4"
    >
      {({ errors, touched, values, setValues, setTouched, handleSubmit }) => {
        return (
          <>
            {mutation.isLoading && <LoadingOverlay />}
            <Form onSubmit={handleSubmit}>
              <h1 className="fancy mb-4">
                Please tell us why you&apos;re closing this job
              </h1>
              <Field
                as={RadioGroup}
                id="project_cancel_reasons"
                name="project_cancel_reasons"
                onChange={e => handleRadioChange(e, setValues, setTouched)}
                values={PROJECT_CANCEL_REASONS}
              />
              {values.other && (
                <Field
                  as={TextArea}
                  labelText="Please tell us why you are closing this role."
                  id="reason"
                  name="reason"
                  touched={touched}
                  errors={errors}
                />
              )}
              <div className="text-right">
                <Button
                  className="mr-4"
                  data-id="remove-job-btn"
                  data-testid="remove-job-button"
                  onClick={onCancel}
                  size="sm"
                  type="button"
                  secondary
                >
                  Cancel
                </Button>
                <Button
                  data-id="remove-job-btn"
                  data-testid="remove-job-button"
                  disabled={!values.reason}
                  size="sm"
                  type="submit"
                >
                  Close job
                </Button>
              </div>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

CloseProjectForm.propTypes = {
  slug: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CloseProjectForm;
